import { EntityTypes } from '../entity';
import { DocumentEditMode } from '../userSetting';
import { AITaskSessionApproval } from './aiTaskSession';
import { CopilotPanelContentBase, CopilotPanelContext } from './copilotBridge';
import { CopilotConversation } from './copilotConversation';
import { CopilotFieldRef } from './copilotField';
import { CopilotScene } from './copilotScene';

export class CopilotGenericContent extends CopilotPanelContentBase {
  public readonly context = CopilotPanelContext.Generic;

  sessionId?: number;

  contextKey?: string;

  // set by a list scene and the dashboard; a detail scene sets entityType/entityId instead
  scene?: CopilotScene;

  entityType?: EntityTypes;

  entityId?: number;

  // Draft automation of the entity (a control or requirement), when the scene judged it available (isAutomationDraftingAvailable)
  canDraftAutomation?: boolean;

  // Audit with AI of the entity (an asset, risk, process, objective or processing activity), when the scene judged it
  // available (isEntityEvidenceAuditAvailable)
  canAuditEvidence?: boolean;

  // the asset's type on the asset dossier: a Supplier offers its two investigations (PLAN-073)
  assetType?: number;

  conversation: CopilotConversation;

  approvals: AITaskSessionApproval[];

  selectedDocument?: CopilotSelectedDocument;

  // Reference to the tenant-authored library instruction that should run. The backend resolves the
  // instruction from the database and runs its text; the client never sends the instruction itself.
  instructionExternalId?: string;

  // the field a field action started the session on, on a session opened from the history
  originField?: CopilotFieldRef;

  constructor() {
    super();

    this.conversation = new CopilotConversation();
    this.approvals = [];
  }
}

// The session key of a Generic registration: one per scene, so a scene restores and lists only its own sessions. A
// list scene and the dashboard are keyed by their scene ("Generic.Dashboard"), a detail scene by the entity type it
// shows ("Generic.Asset"). Undefined for a content that names neither: such a scene starts a new session and never
// restores one.
export const getGenericSessionContextKey = (content: CopilotPanelContentBase | undefined): string | undefined => {
  if (!(content instanceof CopilotGenericContent)) return undefined;
  if (content.scene !== undefined) {
    return `${CopilotPanelContext[CopilotPanelContext.Generic]}.${CopilotScene[content.scene]}`;
  }
  if (content.entityType !== undefined) {
    return `${CopilotPanelContext[CopilotPanelContext.Generic]}.${EntityTypes[content.entityType]}`;
  }

  return undefined;
};

export class CopilotSelectedDocument {
  contentBlockId?: string;

  version?: number;

  title?: string;

  isDocumentEditorSession?: boolean;

  // How the agent should apply its edits: 'tracked' records them as tracked changes, 'direct' applies them
  // immediately. Sourced from the user's documentEditModeAI setting so the backend can steer the agent.
  documentEditMode?: DocumentEditMode;

  // The user's current text selection in the document, so the agent can ground "change this" on the exact
  // highlight. Undefined when nothing is selected. Anchors are best-effort (SuperDoc: SelectionTarget
  // blockId+offset) and supplementary to the text.
  selectionText?: string;

  selectionStartBlockId?: string;

  selectionStartOffset?: number;

  selectionEndBlockId?: string;

  selectionEndOffset?: number;
}
